import { type PortableTextBlock, stegaClean } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { Group as GroupType } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

type GroupProps = {
  value: GroupType & { _key?: string }
  documentId?: string
  documentType?: string
  parentPath?: string
}

export function Group({ value, documentId, documentType, parentPath }: GroupProps) {
  const { _key, content, gap, align, justify, columns } = value

  if (!content?.length) {
    return null
  }

  // Column settings
  const columnsDefault = stegaClean(columns?.default) || 1
  const columnsMd = stegaClean(columns?.md)
  const columnsLg = stegaClean(columns?.lg)

  // Fit content settings
  const fitContentDefault = stegaClean(columns?.fitContentDefault)
  const fitContentMd = stegaClean(columns?.fitContentMd)
  const fitContentLg = stegaClean(columns?.fitContentLg)

  // Determine if we should use flex at each breakpoint
  const useFlexDefault = columnsDefault > 1 && fitContentDefault
  const useFlexMd = columnsMd && columnsMd > 1 && fitContentMd
  const useFlexLg = columnsLg && columnsLg > 1 && fitContentLg

  // Layout classes - switch between grid and flex per breakpoint
  const layoutClasses = cn(
    useFlexDefault ? 'flex flex-row flex-wrap' : 'grid',
    useFlexMd !== undefined && (useFlexMd ? 'md:flex md:flex-row md:flex-wrap' : 'md:grid'),
    useFlexLg !== undefined && (useFlexLg ? 'lg:flex lg:flex-row lg:flex-wrap' : 'lg:grid'),
  )

  // Grid columns classes (only apply when not using flex)
  const gridColsClasses = cn(
    !useFlexDefault && `grid-cols-${columnsDefault}`,
    !useFlexMd && columnsMd && `md:grid-cols-${columnsMd}`,
    !useFlexLg && columnsLg && `lg:grid-cols-${columnsLg}`,
  )

  // Gap classes
  const gapDefault = stegaClean(gap?.default) || 'md'
  const gapMd = stegaClean(gap?.md)
  const gapLg = stegaClean(gap?.lg)

  const gapClasses = cn(`gap-${gapDefault}`, gapMd && `md:gap-${gapMd}`, gapLg && `lg:gap-${gapLg}`)

  // Align classes (different for grid vs flex)
  const getAlignClass = (useFlex: boolean, value: string) => {
    if (useFlex) {
      return {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
        stretch: 'items-stretch',
      }[value]
    }
    return {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    }[value]
  }

  // Justify classes (different for grid vs flex)
  const getJustifyClass = (useFlex: boolean, value: string) => {
    if (useFlex) {
      return {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        stretch: 'justify-start',
      }[value]
    }
    return {
      start: 'justify-items-start',
      center: 'justify-items-center',
      end: 'justify-items-end',
      stretch: 'justify-items-stretch',
    }[value]
  }

  // Extract responsive align/justify values
  const alignDefault = stegaClean(align?.default)
  const alignMd = stegaClean(align?.md)
  const alignLg = stegaClean(align?.lg)

  const justifyDefault = stegaClean(justify?.default)
  const justifyMd = stegaClean(justify?.md)
  const justifyLg = stegaClean(justify?.lg)

  // Responsive align classes
  const alignClasses = cn(
    alignDefault && getAlignClass(!!useFlexDefault, alignDefault),
    alignMd &&
      `md:${getAlignClass(useFlexMd !== undefined ? !!useFlexMd : !!useFlexDefault, alignMd)}`,
    alignLg &&
      `lg:${getAlignClass(useFlexLg !== undefined ? !!useFlexLg : !!useFlexDefault, alignLg)}`,
  )

  // Responsive justify classes
  const justifyClasses = cn(
    justifyDefault && getJustifyClass(!!useFlexDefault, justifyDefault),
    justifyMd &&
      `md:${getJustifyClass(useFlexMd !== undefined ? !!useFlexMd : !!useFlexDefault, justifyMd)}`,
    justifyLg &&
      `lg:${getJustifyClass(useFlexLg !== undefined ? !!useFlexLg : !!useFlexDefault, justifyLg)}`,
  )

  // Create data attribute for presentation mode if we have all required context
  const attr =
    _key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${_key}"]`,
        })
      : undefined

  return (
    <div
      className={cn(layoutClasses, gridColsClasses, gapClasses, alignClasses, justifyClasses)}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      <PortableText
        value={content as PortableTextBlock[]}
        tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
        asFragment
        documentId={documentId}
        documentType={documentType}
        parentPath={_key && parentPath ? `${parentPath}[_key=="${_key}"].content` : parentPath}
      />
    </div>
  )
}
